import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { ChevronLeft, Copy, Check } from "lucide-react";
import { useWallet } from "@/context/WalletContext";
import { CHAINS, addressFor } from "@/lib/chains";
import ChainIcon from "@/components/ChainIcon";

export default function Receive() {
  const nav = useNavigate();
  const { accounts } = useWallet();
  const [params] = useSearchParams();
  const [chainId, setChainId] = useState(params.get("chain") || "btc");
  const [copied, setCopied] = useState(false);
  const chain = CHAINS.find((c) => c.id === chainId);
  const address = addressFor(accounts, chainId);

  const copy = () => {
    navigator.clipboard.writeText(address);
    setCopied(true);
    toast.success("Address copied");
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="px-5 pt-6">
      <button onClick={() => nav(-1)} className="mb-5 flex items-center gap-1 text-[#9CA3AF] hover:text-white">
        <ChevronLeft className="h-5 w-5" /> <span className="font-sans text-sm">Back</span>
      </button>
      <h1 className="font-outfit text-2xl font-extrabold">Receive</h1>

      {/* chain selector */}
      <div className="mt-5 flex gap-2 overflow-x-auto pb-1">
        {CHAINS.map((c) => (
          <button
            key={c.id}
            data-testid={`receive-chain-${c.id}`}
            onClick={() => setChainId(c.id)}
            className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 font-outfit text-xs font-semibold transition-colors ${
              chainId === c.id ? "border-neon bg-neon/10 text-neon" : "border-[#1F2430] text-[#9CA3AF]"
            }`}
          >
            {c.symbol}
          </button>
        ))}
      </div>

      <div className="mt-6 flex flex-col items-center card-surface p-6 fade-up">
        <div className="mb-4 flex items-center gap-2">
          <ChainIcon chain={chain} size={34} />
          <span className="font-outfit text-sm font-semibold">{chain.name}</span>
        </div>
        <div className="relative rounded-2xl bg-white p-4">
          <QRCodeSVG data-testid="receive-qr-code-image" value={address} size={200} level="M" />
          {/* neon corners */}
          {["-top-1 -left-1 border-t-2 border-l-2", "-top-1 -right-1 border-t-2 border-r-2", "-bottom-1 -left-1 border-b-2 border-l-2", "-bottom-1 -right-1 border-b-2 border-r-2"].map((cls, i) => (
            <span key={i} className={`absolute h-5 w-5 rounded-sm border-neon ${cls}`} />
          ))}
        </div>

        <p className="mt-5 break-all text-center font-rajdhani text-sm text-white" data-testid="receive-address-text">{address}</p>
        <button
          data-testid="copy-receive-address-button"
          onClick={copy}
          className="mt-4 flex items-center gap-2 rounded-full bg-neon px-5 py-2.5 font-outfit text-sm font-bold text-[#08090C] active:scale-95 neon-glow"
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "Copied" : "Copy address"}
        </button>
      </div>

      <p className="mt-4 text-center font-sans text-xs text-[#6B7280]">
        Send only {chain.symbol} {chain.family === "evm" ? `(${chain.name})` : ""} to this address.
      </p>
    </div>
  );
}
